import { notifications } from '@mantine/notifications'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import { usePollingQuery } from '@/hooks/usePollingQuery'
import type { PaymentMethod, Plan, PurchaseCreditsResponse } from '@/services/api/api.types'

interface UseCreditPurchaseProps {
  plans: Plan[]
  paymentMethod?: PaymentMethod | null
  currentAutoRefillEnabled: boolean
  currentCredits: number
  onPurchaseComplete: () => Promise<void>
  onClose: () => void
}

export function useCreditPurchase({
  plans,
  paymentMethod = null,
  currentAutoRefillEnabled,
  currentCredits,
  onPurchaseComplete,
  onClose,
}: UseCreditPurchaseProps) {
  const { api } = useGlobalState()
  const { pollUntilCondition } = usePollingQuery()

  // State management
  const [selectedPlan, setSelectedPlan] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)

  // Derived state
  const selectedPlanData = plans?.find(p => p.bundleId === selectedPlan)
  const hasPaymentMethod = paymentMethod !== null

  // Polling strategy to wait for credit balance update after purchase
  const pollForCreditUpdate = useCallback(
    async (expectedIncrease: number, initialCredits: number) => {
      return await pollUntilCondition({
        queryKey: ['billing', 'info'],
        condition: (data: any) => {
          const updatedBillingInfo = data?.billingInfo
          const updatedCredits = updatedBillingInfo?.packageCredits ?? 0

          return updatedCredits >= initialCredits + expectedIncrease
        },
        logContext: 'Credit purchase polling',
      })
    },
    [pollUntilCondition],
  )

  // Shared success handler for credit purchases
  const handlePurchaseSuccess = async (planId: string) => {
    setIsUpdating(true)
    try {
      const selectedPlan = plans?.find(p => p.bundleId === planId)
      if (!selectedPlan) {
        throw new Error('Selected plan not found')
      }

      // Start polling for credit update
      const pollSuccess = await pollForCreditUpdate(selectedPlan.creditsPerBundle, currentCredits)

      // Call parent completion handler
      await onPurchaseComplete()

      // Show appropriate notification
      if (pollSuccess) {
        notifications.show({
          title: 'Purchase Successful',
          message: `${selectedPlan.creditsPerBundle.toLocaleString()} credits added to your account`,
          color: 'green',
        })
      } else {
        notifications.show({
          title: 'Purchase Processing',
          message: `Your purchase is complete. Credits may take a few moments to appear in your account.`,
          color: 'blue',
        })
      }

      onClose()
    } catch (error) {
      console.error('Failed to handle credit purchase completion:', error)
      notifications.show({
        title: 'Purchase Error',
        message:
          'There was an issue processing your credit purchase. Please contact support if credits were not added.',
        color: 'red',
      })
    } finally {
      setIsUpdating(false)
    }
  }

  // Shared success handler for both mutations
  const handleMutationSuccess = (response: PurchaseCreditsResponse) => {
    if (response.purchased) {
      handlePurchaseSuccess(selectedPlan)
    } else if (response.clientSecret) {
      setClientSecret(response.clientSecret)
      setShowPaymentForm(true)
    } else {
      notifications.show({
        title: 'Purchase Failed',
        message: 'Unable to purchase credits. Please try again.',
        color: 'red',
      })
    }
  }

  // Shared error handler for both mutations
  const handleMutationError = (error: any, context: 'setup' | 'purchase') => {
    const errorMessage =
      context === 'setup' ? 'Failed to setup credit purchase payment:' : 'Failed to purchase credits:'
    console.error(errorMessage, error)
    // Error display is handled by component via error state, not notifications
  }

  // Purchase credits without stored payment method (triggers Stripe payment flow)
  const purchaseCreditsNoPaymentMethodMutation = useMutation({
    mutationFn: async () => {
      const plan = plans?.find(p => p.bundleId === selectedPlan)
      if (!plan) throw new Error('Plan not found')

      return await api.purchaseCreditsNoPaymentMethod({
        bundle_id: plan.bundleId,
        auto_refill: currentAutoRefillEnabled,
      })
    },
    onSuccess: handleMutationSuccess,
    onError: error => handleMutationError(error, 'setup'),
  })

  // Purchase credits using stored payment method (direct charge)
  const purchaseCreditsWithPaymentMethodMutation = useMutation({
    mutationFn: async () => {
      const plan = plans?.find(p => p.bundleId === selectedPlan)
      if (!plan) throw new Error('Plan not found')

      return await api.purchaseCreditsWithPaymentMethod({
        bundle_id: plan.bundleId,
        auto_refill: currentAutoRefillEnabled,
      })
    },
    onSuccess: handleMutationSuccess,
    onError: error => handleMutationError(error, 'purchase'),
  })

  // Reset state when modal opens
  const resetModalState = useCallback(() => {
    const activePlans = plans?.filter(p => p.active) || []
    const cheapestPlan = activePlans.sort((a, b) => a.priceDollars - b.priceDollars)[0]

    // Default to cheapest active plan for one-time purchases
    setSelectedPlan(cheapestPlan?.bundleId || '')

    // Reset payment flow state
    setShowPaymentForm(false)
    setClientSecret(null)
    setIsUpdating(false)
  }, [plans])

  // Event handlers
  const handlePlanSelect = useCallback((planId: string) => {
    setSelectedPlan(planId)
  }, [])

  const handlePurchase = useCallback(async () => {
    if (!selectedPlan) return

    const plan = plans?.find(p => p.bundleId === selectedPlan)
    if (!plan) return

    if (hasPaymentMethod) {
      purchaseCreditsWithPaymentMethodMutation.mutate()
    } else {
      purchaseCreditsNoPaymentMethodMutation.mutate()
    }
  }, [
    selectedPlan,
    plans,
    hasPaymentMethod,
    purchaseCreditsWithPaymentMethodMutation,
    purchaseCreditsNoPaymentMethodMutation,
  ])

  const handlePaymentSuccess = useCallback(async () => {
    if (!selectedPlan) return
    await handlePurchaseSuccess(selectedPlan)
  }, [selectedPlan, handlePurchaseSuccess])

  const handleBackFromPayment = useCallback(() => {
    setShowPaymentForm(false)
    setClientSecret(null)
  }, [])

  // Computed loading state
  const isLoading =
    isUpdating || purchaseCreditsNoPaymentMethodMutation.isPending || purchaseCreditsWithPaymentMethodMutation.isPending

  // Computed error state with user-friendly messages
  const getErrorMessage = () => {
    if (purchaseCreditsNoPaymentMethodMutation.error) {
      return 'Unable to set up payment for credit purchase. Please check your payment details and try again.'
    }
    if (purchaseCreditsWithPaymentMethodMutation.error) {
      return 'Unable to purchase credits. Please verify your payment method is valid and try again.'
    }
    return null
  }

  const error = getErrorMessage()

  return {
    // State
    selectedPlan,
    selectedPlanData,
    showPaymentForm,
    clientSecret,
    hasPaymentMethod,
    isLoading,
    error,

    // Mutations (exposed for granular error handling in components)
    purchaseCreditsNoPaymentMethodMutation,
    purchaseCreditsWithPaymentMethodMutation,

    // Event handlers
    handlePlanSelect,
    handlePurchase,
    handlePaymentSuccess,
    handleBackFromPayment,
    resetModalState,
  }
}
